import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../components/ui/table";
import { Bell, CheckCircle, History, X } from "lucide-react";
import { API } from "../../../App";

const SEVERITY_META = {
  critical: { label: "Crítico", color: "text-red-300",   bg: "bg-red-500/15",   border: "border-red-500/40" },
  warn:     { label: "Aviso",   color: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/40" },
  info:     { label: "Info",    color: "text-cyan-300",  bg: "bg-cyan-500/15",  border: "border-cyan-500/40" },
};

export function AuditLogDialog({ open, instructor, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !instructor) return;
    const run = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/di/audit-log`, { withCredentials: true, params: { instructor_id: instructor.id, limit: 200 } });
        setEntries(r.data || []);
      } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
      finally { setLoading(false); }
    };
    run();
  }, [open, instructor]);

  if (!instructor) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-cockpit-panel border-cockpit-border w-[95vw] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="di-audit-dialog">
        <DialogHeader>
          <DialogTitle className="text-orange-300 flex items-center gap-2">
            <History className="w-5 h-5" /> Auditoria — {instructor.name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-slate-500 text-sm">A carregar...</div>
        ) : entries.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm border border-dashed border-cockpit-border rounded">Sem eventos registados.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-cockpit-border">
                <TableHead>Data/Hora (UTC)</TableHead><TableHead>Ação</TableHead><TableHead>Tipo</TableHead><TableHead>Autor</TableHead><TableHead>Campos alterados</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} className="border-cockpit-border">
                  <TableCell className="font-mono text-[11px]">{e.ts.slice(0, 19).replace("T", " ")}</TableCell>
                  <TableCell><span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${e.action === "create" ? "bg-emerald-500/15 text-emerald-300" : e.action === "delete" ? "bg-red-500/15 text-red-300" : "bg-cyan-500/15 text-cyan-300"}`}>{e.action}</span></TableCell>
                  <TableCell className="text-xs">{e.scope}</TableCell>
                  <TableCell className="text-xs text-slate-400">{e.actor?.email || e.actor?.user_id || "—"}</TableCell>
                  <TableCell className="text-[11px] text-slate-300 font-mono">{(e.diff || []).slice(0, 6).join(", ")}{(e.diff || []).length > 6 ? ` +${e.diff.length - 6}` : ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Faixa de alertas no topo: instrutores (separador Instrutores) ou pilotos (separador Pilotos; `pilots` dá os nomes)
export default function AlertsBanner({ target = "instructor", instructors = [], pilots = [], onJumpToInstructor, onJumpToPilot }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/di/alerts`, { withCredentials: true, params: { include_dismissed: true, target } });
      setAlerts(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a carregar alertas"); }
    finally { setLoading(false); }
  }, [target]);

  useEffect(() => { refresh(); }, [refresh]);

  const dismiss = async (id) => {
    try { await axios.post(`${API}/di/alerts/${id}/dismiss`, {}, { withCredentials: true }); toast.success("Alerta silenciado"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro a silenciar"); }
  };
  const restore = async (id) => {
    try { await axios.post(`${API}/di/alerts/${id}/restore`, {}, { withCredentials: true }); toast.success("Alerta restaurado"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const counts = alerts.reduce((acc, a) => { if (a.dismissed_at) return acc; acc[a.severity] = (acc[a.severity] || 0) + 1; return acc; }, {});
  const total = (counts.critical || 0) + (counts.warn || 0) + (counts.info || 0);
  const dismissedCount = alerts.filter((a) => a.dismissed_at).length;
  const instrName = (id) => instructors.find((i) => i.id === id)?.name || id;
  const pilotName = (id) => pilots.find((p) => p.id === id)?.name || "Piloto";

  const ToggleBtn = () => (
    <button type="button"
      className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded border ${showDismissed ? "bg-slate-800 text-slate-300 border-slate-700" : "bg-slate-900/40 text-slate-500 border-slate-800 hover:bg-slate-800"}`}
      onClick={() => setShowDismissed((v) => !v)} data-testid="di-alerts-toggle-dismissed">
      {showDismissed ? "Ocultar silenciados" : "Mostrar silenciados"}
    </button>
  );

  const AlertRow = ({ a, opacity }) => {
    const meta = SEVERITY_META[a.severity] || SEVERITY_META.info;
    // Pilotos: a mensagem dos documentos já traz o nome («Nome: Licença ...»), tira-se aí porque o nome vai à frente
    const doPiloto = target === "pilot";
    const quem = doPiloto ? pilotName(a.pilot_id) : instrName(a.instructor_id);
    const texto = doPiloto
      ? (a.message.startsWith(`${quem}: `) ? a.message.slice(quem.length + 2) : a.message)
      : a.message.replace(/^[^—]*— /, "");
    return (
      <div key={a.id} className={`flex items-center gap-2 p-2 rounded border ${meta.border} ${meta.bg} ${opacity || ""}`} data-testid={`di-alert-${a.id}`}>
        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${meta.color} bg-black/30`}>{meta.label}</span>
        <button type="button" onClick={() => (doPiloto ? onJumpToPilot?.(a.pilot_id) : onJumpToInstructor?.(a.instructor_id))} className="text-xs text-slate-200 hover:text-orange-300 text-left flex-1 truncate" title={a.message}>
          <span className="font-semibold">{quem}</span> · {texto}
        </button>
        <span className="text-[11px] font-mono text-slate-400 whitespace-nowrap">{a.days_remaining == null ? "" : a.days_remaining >= 0 ? `${a.days_remaining}d` : `−${-a.days_remaining}d`}</span>
        {a.dismissed_at ? (
          <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => restore(a.id)}>Restaurar</Button>
        ) : (
          <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-500 hover:text-slate-200" onClick={() => dismiss(a.id)} title="Silenciar" data-testid={`di-alert-dismiss-${a.id}`}><X className="w-3.5 h-3.5" /></Button>
        )}
      </div>
    );
  };

  if (!loading && total === 0) {
    return (
      <Card className="instrument-panel border-emerald-500/30" data-testid="di-alerts-banner">
        <CardContent className="py-4 flex items-center gap-3 text-sm flex-wrap">
          <CheckCircle className="w-5 h-5 text-emerald-400" />
          <span className="text-emerald-300 flex-1" data-testid="di-alerts-empty">
            {showDismissed && dismissedCount > 0 ? `${dismissedCount} alerta(s) silenciado(s) — clica em 'Restaurar' para reativar.` : "Sem alertas ativos. Toda a equipa está dentro dos prazos."}
          </span>
          <ToggleBtn />
        </CardContent>
        {showDismissed && dismissedCount > 0 && (
          <CardContent className="pt-0">
            <div className="space-y-1.5">{alerts.filter((a) => a.dismissed_at).map((a) => <AlertRow key={a.id} a={a} opacity="opacity-60" />)}</div>
          </CardContent>
        )}
      </Card>
    );
  }

  return (
    <Card className="instrument-panel" data-testid="di-alerts-banner">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2 text-orange-300">
            <Bell className="w-5 h-5" />
            Alertas de validade
            <span className="text-[11px] font-normal text-slate-400">({counts.critical || 0} crítico · {counts.warn || 0} aviso · {counts.info || 0} info{dismissedCount > 0 ? ` · ${dismissedCount} silenciado(s)` : ""})</span>
          </CardTitle>
          <ToggleBtn />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {alerts.filter((a) => showDismissed || !a.dismissed_at).map((a) => <AlertRow key={a.id} a={a} opacity={a.dismissed_at ? "opacity-50" : ""} />)}
        </div>
      </CardContent>
    </Card>
  );
}
